import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import moment from 'moment';

// API Imports
import { 
    getDeliveryTrailByBomId, 
    updateDeliveryStep, 
    getS3PresignedUrl, 
    getBomById, 
    getCustomerById 
} from 'lib/axiosUtils';

// Component Imports
import ShowToast from 'components/ShowToast';
import Spinner from 'components/Spinner';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL } from 'assets/constants/Constants';
import { HTTP_HEADER_KEYS, HTTP_CONTENT_TYPES } from 'assets/constants/apiConstants';

// MUI Imports
import {
    Stepper, Step, StepLabel, StepContent,
    Button, Typography, TextField, Box, Chip, Grid, 
    Divider, LinearProgress, IconButton, Paper, Tooltip, Card
} from '@mui/material';

// Icons
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';

// CSS
import '../BOM/bom.css'; 
import './delivery_trail.css'; 
import { LocalShipping } from '@mui/icons-material';

const CreateDeliveryTrail = () => {
    const { bomId } = useParams();
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);
    const [trailData, setTrailData] = useState(null);
    const [activeStepIndex, setActiveStepIndex] = useState(0);

    // Form States for the ACTIVE step
    const [remarks, setRemarks] = useState('');
    const [filesDraft, setFilesDraft] = useState([]); 
    const [uploadedAttachments, setUploadedAttachments] = useState([]); 
    const [isUploading, setIsUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);

    const activeStepRef = useRef(null);

    useEffect(() => {
        if (bomId) fetchTrailData();
    }, [bomId]);

    // Scroll active step into view when it changes
    useEffect(() => {
        if (activeStepRef.current) {
            setTimeout(() => {
                activeStepRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
    }, [activeStepIndex]);

    const fetchTrailData = async () => {
        setIsLoading(true);
        try {
            const response = await getDeliveryTrailByBomId({}, bomId);
            if (response?.data?.result) {
                const data = response.data.result;

                // Enrich BOM/Customer Data if needed
                if (!data.bom_id || !data.bom_id.customer_id) {
                    try {
                        const bomRes = await getBomById({}, {}, bomId);
                        if (bomRes?.data?.result) data.bom_id = { ...(data.bom_id || {}), ...bomRes.data.result };
                    } catch (e) {}
                }
                if (data.bom_id?.customer_id && typeof data.bom_id.customer_id === 'string') {
                    try {
                        const custRes = await getCustomerById({}, {}, data.bom_id.customer_id);
                        if (custRes?.data?.result) data.bom_id.customer_id = custRes.data.result;
                    } catch (e) {}
                }

                setTrailData(data);
                
                // Determine initial active step
                if (data.steps) {
                    const currentIndex = data.steps.findIndex(s => s.status === 'Pending' || s.status === 'In Progress');
                    
                    // FIX: If currentIndex is -1 (All done), set target to -1 (Collapse all)
                    // Previous code set it to length-1, which kept the last step open.
                    const target = currentIndex !== -1 ? currentIndex : -1;
                    
                    handleStepChange(target, data.steps);
                }
            }
        } catch (error) {
            console.error(error);
            ShowToast('Failed to load data', TYPE_FAIL, INTERVAL);
        } finally {
            setIsLoading(false);
        }
    };
    // Helper to switch active step and populate data
// Helper to switch active step and populate data
    const handleStepChange = (index, stepsArray = null) => {
        // FIX: Toggle logic - if clicking the currently active step, collapse it.
        if (activeStepIndex === index) {
            setActiveStepIndex(-1);
            return;
        }

        // Standard logic to open a step
        const steps = stepsArray || trailData?.steps;
        
        // If index is -1, just reset state (used for initial load when all completed)
        if (index === -1) {
            setActiveStepIndex(-1);
            return;
        }

        if (!steps || !steps[index]) return;

        setActiveStepIndex(index);
        
        // Populate form with existing data
        setRemarks(steps[index].remarks || '');
        setFilesDraft([]); 
    };

    const handleFileSelect = (e) => {
        if (e.target.files) {
            setFilesDraft([...filesDraft, ...Array.from(e.target.files)]);
        }
    };

    const removeDraftFile = (index) => {
        setFilesDraft(filesDraft.filter((_, i) => i !== index));
    };

    const uploadFiles = async () => {
        if (filesDraft.length === 0) return [];
        setIsUploading(true);
        const uploaded = [];
        try {
            for (let i = 0; i < filesDraft.length; i++) {
                const file = filesDraft[i];
                const presignedRes = await getS3PresignedUrl({}, {
                    fileName: file.name,
                    fileType: file.type || 'application/octet-stream'
                });
                
                const putUrl = presignedRes?.data?.url;
                const publicUrl = presignedRes?.data?.fileUrl;

                if (putUrl) {
                    await new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.open('PUT', putUrl, true);
                        xhr.setRequestHeader(HTTP_HEADER_KEYS.CONTENT_TYPE, file.type || HTTP_CONTENT_TYPES.OCTET_STREAM);
                        xhr.upload.onprogress = (e) => {
                            if (e.lengthComputable) {
                                const pct = Math.round(((i + (e.loaded / e.total)) / filesDraft.length) * 100);
                                setUploadPct(pct);
                            }
                        };
                        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject());
                        xhr.onerror = () => reject();
                        xhr.send(file);
                    });

                    // Extract S3 Key
                    let file_key = '';
                    try {
                        const u = new URL(putUrl);
                        const path = decodeURIComponent(u.pathname.replace(/^\/+/, ''));
                        const at = path.indexOf('uploads/');
                        file_key = at >= 0 ? path.substring(at) : path;
                    } catch (e) {}

                    uploaded.push({
                        file_name: file.name,
                        file_type: file.type,
                        file_url: publicUrl,
                        file_key: file_key
                    });
                }
            }
        } catch (err) {
            ShowToast('File upload failed', TYPE_FAIL, INTERVAL);
            setIsUploading(false);
            return null;
        } finally {
            setIsUploading(false);
            setUploadPct(0);
        }
        return uploaded;
    };

const handleUpdateStep = async (status) => {
        if (!trailData) return;
        
        // ... (Keep your existing file upload logic here) ...
        let newAttachments = [];
        if (filesDraft.length > 0) {
            const uploaded = await uploadFiles();
            if (!uploaded) return; 
            newAttachments = uploaded;
        }

        try {
            const stepObj = trailData.steps[activeStepIndex];
            const finalAttachments = [...(stepObj.attachments || []), ...newAttachments];

            const payload = {
                status: status,
                remarks: remarks,
                attachments: finalAttachments
            };

            const response = await updateDeliveryStep({}, bomId, stepObj.name, payload);
            
            if (response?.status === 200 || response?.data?.result) {
                ShowToast(`Step marked as ${status}`, TYPE_SUCCESS, INTERVAL);
                
                const refreshRes = await getDeliveryTrailByBomId({}, bomId);
                if (refreshRes?.data?.result) {
                    const newData = refreshRes.data.result;
                    newData.bom_id = trailData.bom_id;
                    setTrailData(newData);

                    if (status === 'Completed' || status === 'Skipped') {
                         const nextIndex = activeStepIndex + 1;
                         if (nextIndex < newData.steps.length) {
                             handleStepChange(nextIndex, newData.steps);
                         } else {
                             // FIX: If we just finished the last step, collapse everything (-1)
                             // Previous code kept activeStepIndex same, keeping it open.
                             handleStepChange(-1, newData.steps); 
                         }
                    } else {
                        handleStepChange(activeStepIndex, newData.steps);
                    }
                }
            }
        } catch (error) {
            console.error(error);
            ShowToast('Update failed', TYPE_FAIL, INTERVAL);
        }
    };
    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'success';
            case 'Issue Reported': return 'error';
            case 'In Progress': return 'primary';
            case 'Skipped': return 'warning';
            default: return 'default';
        }
    };

    const CustomStepIcon = (props) => {
        const { active, completed, error } = props;
        if (error) return <ErrorIcon color="error" fontSize="large" />;
        if (completed) return <CheckCircleIcon color="success" fontSize="large" />;
        if (active) return <PlayCircleFilledIcon style={{ color: '#2d3d42' }} fontSize="large" />;
        return <RadioButtonUncheckedIcon color="disabled" fontSize="large" />;
    };

    if (!trailData) return <Spinner />;

    // Logic to determine accessibility of steps
    // A step is accessible if it is before or is the current "Pending" step
    const firstPendingIndex = trailData.steps.findIndex(s => s.status === 'Pending');
    const maxAccessibleIndex = firstPendingIndex === -1 ? trailData.steps.length - 1 : firstPendingIndex;

    return (
        // 1. MAIN CONTAINER: Fixed Height 100vh
        <div className="main-content delivery-trail-page" style={{ 
            height: '100vh', 
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden',
            paddingBottom: 0 // Override generic padding
        }}>
            
            {/* 2. HEADER: Fixed Height */}
            <div style={{ flexShrink: 0, paddingBottom: '15px' }}>
                <div className="pagetitle row" style={{ color: 'black', paddingTop: 15, margin: 0 }}>
                    <div className="col-md-10 sm_device">
                        <LocalShippingIcon sx={{ mr: 1.5, fontSize: 26, color: '#2d3d42' }} /> 
                        Delivery Tracking
                        {trailData.bom_id?.fixture_document_number && 
                            <span style={{ fontSize: '14px', color: '#666', fontWeight: 400, marginLeft: 10 }}>
                                | {trailData.bom_id.fixture_document_number}
                            </span>
                        }
                    </div>
                    <div className="col-md-2 sm_device" style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: '#666' }} /> 
                        <span style={{fontSize: '14px', color: '#666'}}>{moment().format('DD MMM YYYY')}</span>
                    </div>
                </div>
                <div className="bom-divider" style={{ margin: '15px 15px 0 15px' }} />
            </div>

            {/* 3. CONTENT AREA: Fills remaining space, No outer scroll */}
            <div className="container-fluid" style={{ flex: 1, overflow: 'hidden', padding: '15px 25px 25px 25px' }}>
                <Grid container spacing={3} style={{ height: '100%' }}>
                    
                    {/* LEFT COLUMN: Summary Card */}
                    <Grid item xs={12} md={4} style={{ height: '100%' }}>
                        <div className="project-summary-card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div className="summary-header" style={{ flexShrink: 0 }}>
                                <h3 className="summary-title">
                                    <DescriptionIcon fontSize="small" /> Project Details
                                </h3>
                            </div>
                            
                            {/* SCROLLABLE SUMMARY CONTENT */}
                            <div className="summary-content" style={{ flex: 1, overflowY: 'auto' }}>
                                <div className="summary-row">
                                    <span className="summary-label">Customer</span>
                                    <span className="summary-value">
                                        {trailData.bom_id?.customer_id?.customer_name || 'N/A'}
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span className="summary-label">Fixture Name</span>
                                    <span className="summary-value">
                                        {trailData.bom_id?.fixture_name || 'N/A'}
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span className="summary-label">Current Stage</span>
                                    <Chip 
                                        label={trailData.current_step || 'Completed'} 
                                        size="small"
                                        className="status-chip progress"
                                    />
                                </div>
                                <div className="summary-row" style={{ borderBottom: 'none' }}>
                                    <span className="summary-label">Overall Progress</span>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <Box sx={{ width: '100%', mr: 1 }}>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={Math.round((trailData.steps.filter(s => s.status === 'Completed' || s.status === 'Skipped').length / trailData.steps.length) * 100)} 
                                                sx={{ height: 8, borderRadius: 5, backgroundColor: '#e0e0e0', '& .MuiLinearProgress-bar': { backgroundColor: '#2d3d42' } }}
                                            />
                                        </Box>
                                        <Box sx={{ minWidth: 35 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {Math.round((trailData.steps.filter(s => s.status === 'Completed' || s.status === 'Skipped').length / trailData.steps.length) * 100)}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                </div>

                                {/* <div style={{ marginTop: 20, textAlign: 'center' }}>
                                    <Button 
                                        variant="outlined" 
                                        startIcon={<ArrowBackIcon />}
                                        className="trail-btn-secondary"
                                        onClick={() => history.push('/bom-list')}
                                        fullWidth
                                    >
                                        Back to BOM List
                                    </Button>
                                </div> */}
                            </div>
                        </div>
                    </Grid>

                    {/* RIGHT COLUMN: Timeline */}
                    <Grid item xs={12} md={8} style={{ height: '100%' }}>
                        <div className="trail-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div className="summary-header" style={{ flexShrink: 0 }}>
                                <h3 className="summary-title">
                                    {/* Optional: Add an icon here if you want to match the left side completely */}
                                    <LocalShipping fontSize="small" /> Delivery Trail Timeline
                                </h3>
                            </div>
                            
                            {/* SCROLLABLE TIMELINE */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>                                
                                <Stepper activeStep={activeStepIndex} orientation="vertical">
                                    {trailData.steps.map((step, index) => {
                                        const isIssue = step.status === 'Issue Reported';
                                        const isActive = index === activeStepIndex;
                                        const isCompleted = ['Completed', 'Skipped', 'Issue Reported'].includes(step.status);
                                        const isAccessible = index <= maxAccessibleIndex || trailData.overall_status === 'Completed';

                                        return (
                                            <Step key={step.name} expanded={true} ref={isActive ? activeStepRef : null} completed={isCompleted}>
                                                <StepLabel 
                                                    StepIconComponent={CustomStepIcon}
                                                    error={isIssue}
                                                    sx={{ 
                                                        cursor: isAccessible ? 'pointer' : 'default',
                                                        '&:hover': isAccessible ? { backgroundColor: 'rgba(0,0,0,0.02)' } : {}
                                                    }}
                                                    onClick={() => {
                                                        if (isAccessible) {
                                                            handleStepChange(index);
                                                        }
                                                    }}
                                                >
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                                        <Box display="flex" alignItems="center">
                                                            <Typography variant="subtitle1" fontWeight={isActive ? 700 : 500} style={{ fontSize: isActive ? '17px' : '15px' }}>
                                                                {step.name}
                                                            </Typography>
                                                            
                                                            {/* EDIT BUTTON for Completed Steps */}
                                                            {!isActive && isCompleted && isAccessible && (
                                                                <Tooltip title="Edit this step">
                                                                    <IconButton 
                                                                        size="small" 
                                                                        sx={{ ml: 1, padding: '2px' }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleStepChange(index);
                                                                        }}
                                                                    >
                                                                        <EditIcon fontSize="small" color="action" style={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                        
                                                        {step.status !== 'Pending' && (
                                                            <Chip 
                                                                label={step.status} 
                                                                size="small" 
                                                                color={getStatusColor(step.status)}
                                                                variant={isActive ? "filled" : "outlined"}
                                                                sx={{ fontWeight: 'bold', fontSize: '11px', height: '22px' }}
                                                            />
                                                        )}
                                                    </Box>
                                                </StepLabel>

                                                <StepContent>
                                                    {/* --- VIEW: READ-ONLY (Only if completed AND NOT ACTIVE) --- */}
                                                    {!isActive && isCompleted && (
                                                        <div className="step-history-box" onClick={() => handleStepChange(index)} style={{ cursor: 'pointer' }}>
                                                            <div className="history-meta">
                                                                <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-top' }} color="success" />
                                                                Completed on: {step.completion_date ? moment(step.completion_date).format('DD MMM YYYY, h:mm a') : 'N/A'}
                                                                <span style={{ marginLeft: 10, fontSize: 10, textDecoration: 'underline', color: '#2d3d42' }}>Click to Edit</span>
                                                            </div>
                                                            {step.remarks && (
                                                                <div className="history-remarks">"{step.remarks}"</div>
                                                            )}
                                                            {step.attachments && step.attachments.length > 0 && (
                                                                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                                    {step.attachments.map((file, i) => (
                                                                        <Chip
                                                                            key={i}
                                                                            icon={<AttachFileIcon />}
                                                                            label={file.file_name}
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                window.open(file.file_url, '_blank')
                                                                            }}
                                                                            variant="outlined"
                                                                            clickable
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* --- FORM: ACTIVE (Editing or New) --- */}
                                                    {isActive && (
                                                        <div className="active-step-card">
                                                            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                                                                {isCompleted 
                                                                    ? "You are editing a completed step. Update details below." 
                                                                    : "Please update the tracking details for this stage."}
                                                            </Typography>

                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12}>
                                                                    <label className="step-input-label">Remarks / Comments</label>
                                                                    <TextField
                                                                        fullWidth
                                                                        multiline
                                                                        rows={3}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        placeholder="Enter tracking details, location, or issues..."
                                                                        value={remarks}
                                                                        onChange={(e) => setRemarks(e.target.value)}
                                                                        sx={{ backgroundColor: '#fff' }}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <label className="step-input-label">
                                                                        Attachments 
                                                                        {step.attachments && step.attachments.length > 0 && 
                                                                            <span style={{ fontWeight: 400, color: '#666', marginLeft: 5 }}>
                                                                                ({step.attachments.length} existing)
                                                                            </span>
                                                                        }
                                                                    </label>
                                                                    
                                                                    <div className="trail-dropzone">
                                                                        <Button
                                                                            component="label"
                                                                            startIcon={<CloudUploadIcon />}
                                                                            sx={{ textTransform: 'none' }}
                                                                        >
                                                                            Click to Upload New Files
                                                                            <input type="file" hidden multiple onChange={handleFileSelect} />
                                                                        </Button>
                                                                        
                                                                        {/* Show Draft Files */}
                                                                        {filesDraft.length > 0 && (
                                                                            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                                                                {filesDraft.map((f, i) => (
                                                                                    <Chip
                                                                                        key={i}
                                                                                        label={f.name}
                                                                                        onDelete={() => removeDraftFile(i)}
                                                                                        deleteIcon={<DeleteForeverIcon />}
                                                                                        size="small"
                                                                                        color="primary"
                                                                                        variant="outlined"
                                                                                    />
                                                                                ))}
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        {isUploading && (
                                                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                                                <LinearProgress variant="determinate" value={uploadPct} />
                                                                                <Typography variant="caption" align="center">{uploadPct}% Uploaded</Typography>
                                                                            </Box>
                                                                        )}
                                                                    </div>

                                                                    {/* Show Existing Attachments in Edit Mode */}
                                                                    {step.attachments && step.attachments.length > 0 && (
                                                                         <Box sx={{ mt: 1 }}>
                                                                             <Typography variant="caption" color="textSecondary">Existing Files:</Typography>
                                                                             <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                                                                {step.attachments.map((file, i) => (
                                                                                    <Chip key={i} size="small" variant="outlined" label={file.file_name} icon={<AttachFileIcon />} />
                                                                                ))}
                                                                             </Box>
                                                                         </Box>
                                                                    )}
                                                                </Grid>

                                                                <Grid item xs={12} sx={{ mt: 1 }}>
                                                                    <Divider sx={{ mb: 2 }} />
                                                                    <Box display="flex" gap={2} justifyContent="flex-end">
                                                                        <Button 
                                                                            variant="outlined" 
                                                                            color="error"
                                                                            onClick={() => handleUpdateStep('Issue Reported')}
                                                                            disabled={isUploading}
                                                                        >
                                                                            Report Issue
                                                                        </Button>
                                                                        
                                                                        <Button
                                                                            variant="contained"
                                                                            className="trail-btn-success"
                                                                            onClick={() => handleUpdateStep('Completed')}
                                                                            disabled={isUploading}
                                                                            startIcon={<CheckCircleIcon />}
                                                                        >
                                                                            {isCompleted ? "Update Step" : "Complete Step"}
                                                                        </Button>
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        </div>
                                                    )}
                                                </StepContent>
                                            </Step>
                                        );
                                    })}
                                </Stepper>

                                {trailData.overall_status === 'Completed' && (
                                    <Box sx={{ textAlign: 'center', p: 4, bgcolor: '#f1f8e9', borderRadius: 2, mt: 2 }}>
                                        <CheckCircleIcon sx={{ fontSize: 60, color: '#2e7d32', mb: 2 }} />
                                        <Typography variant="h5" color="#2e7d32" fontWeight="bold">
                                            Delivery Trail Completed
                                        </Typography>
                                    </Box>
                                )}
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default CreateDeliveryTrail;